import { Alert, Box, Button, Divider, Stack, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { QueryAdderCell } from './QueryAdderCell';
import React, { useMemo, useState } from 'react';
import { TrackerDataType } from '../../shared/constants/constants';
import { openSnackbar } from '../AppSnackbar/snackbarSlice';
import { useDispatch } from 'react-redux';
import makeStyles from '@mui/styles/makeStyles';
import {
  bulkUpdateEngagements,
  bulkUpdateInteractions,
  bulkUpdateServices,
} from '../../shared/requests/bulkUpdateRequsts';
import { getBulkEditQueryAdderColumnsByEventHandlerType } from '../../shared/constants/IssueAndServiceKeys';
import findOrganizationFromContact from '../../shared/functions/findOrganizationFromContact';
import { normalizeToIds } from '../../utils/eventHandlerUtils';
import { useQueryClient } from '@tanstack/react-query';

const useStyles = makeStyles({
  button: {
    width: '220px',
    height: '40px',
    fontSize: '16px',
    margin: '0 10px',
    fontWeight: 550,
  },
  divider: { borderColor: 'black', margin: '10px 0' },
  alert: {
    marginBottom: '5px',
  },
  form: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px 5px',
    alignItems: 'center',
    overflowY: 'scroll',
    justifyContent: 'space-between',
    '& > label': {
      width: '100%',
    },
    '& > div': {
      flex: '1 1 220',
    },
  },
});

const sanitizeBulkEditFields = (fields) => {
  for (const key in fields) {
    if (Object.hasOwn(fields, key)) {
      const value = fields[key];
      if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
        delete fields[key];
      }
    }
  }
  return fields;
};

export default function BulkEditQueryAdder({
  selectedBulkEditRowIds,
  eventHandler,
  dropdownTable,
  currMonth,
  rowType,
  onClose,
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const columns = getBulkEditQueryAdderColumnsByEventHandlerType(eventHandler.type);
  const initialBulkEditFieldsState = { entryDate: null, status: undefined };

  const [isSubmitBulkEditButtonLoading, setIsSubmitBulkEditButtonLoading] = useState(false);
  const [bulkEditFields, setBulkEditFields] = useState(initialBulkEditFieldsState);

  const handleCloseBulkEditPopup = () => {
    onClose();
    setBulkEditFields(initialBulkEditFieldsState);
  };

  const handleSubmitBulkEdit = async () => {
    try {
      let bulkEditRequest;
      let snackbarType;
      let invalidateQueryKey;
      switch (eventHandler.type) {
        case TrackerDataType.MEDIA_INTERACTION:
          bulkEditRequest = bulkUpdateInteractions;
          snackbarType = 'interactions';
          invalidateQueryKey = 'monthlyIssues';
          break;
        case TrackerDataType.SERVICE:
          bulkEditRequest = bulkUpdateServices;
          snackbarType = 'services';
          invalidateQueryKey = 'monthlyServices';
          break;
        case TrackerDataType.ENGAGEMENT:
          bulkEditRequest = bulkUpdateEngagements;
          snackbarType = 'engagements';
          invalidateQueryKey = 'monthlyEngagements';
          break;
        default:
          throw new Error('unknown event handler type');
      }

      const preparedFields = { ...bulkEditFields };
      const idMappings = [
        { source: 'communityMember', target: 'communityMembers' },
        { source: 'organization', target: 'organizations' },
      ];
      for (const { source, target } of idMappings) {
        const rawValue = preparedFields[source];
        if (!rawValue) {
          continue;
        }

        const ids = normalizeToIds(rawValue);
        delete preparedFields[source];
        if (ids.length) {
          preparedFields[target] = ids;
        }
      }

      setIsSubmitBulkEditButtonLoading(true);
      await bulkEditRequest(selectedBulkEditRowIds, sanitizeBulkEditFields(preparedFields));
      setIsSubmitBulkEditButtonLoading(false);

      dispatch(openSnackbar({ message: `Edit multiple ${snackbarType} success`, severity: 'success' }));

      await queryClient.invalidateQueries({ queryKey: [invalidateQueryKey, currMonth] });
      handleCloseBulkEditPopup();
    } catch (err) {
      setIsSubmitBulkEditButtonLoading(false);
      handleCloseBulkEditPopup();
      dispatch(openSnackbar({ message: 'Edit failed, please try again', severity: 'error' }));
    }
  };

  const normalizeValues = (input) => {
    if (Array.isArray(input)) {
      return input;
    }
    if (input) {
      return [input];
    }
    return [];
  };

  const handleBulkEditFieldsChange = (val, col) => {
    let newField;
    let autoOrganizationsToAdd = null;
    let organizationIdsAfterChange = null;

    switch (col) {
      case 'organization': {
        const organizations = normalizeValues(val);
        newField = organizations;
        organizationIdsAfterChange = organizations
          .map((organization) => organization?._id ?? organization)
          .filter(Boolean);
        break;
      }
      case 'communityMember': {
        const communityMembers = normalizeValues(val);
        newField = communityMembers;

        if (eventHandler.type === TrackerDataType.ENGAGEMENT) {
          const organizationLookup = dropdownTable.organization || [];
          autoOrganizationsToAdd = communityMembers
            .map((communityMember) => {
              const organizationRef = communityMember?.Organization;
              if (!organizationRef) return null;

              if (typeof organizationRef === 'string') {
                return organizationLookup.find((organization) => organization._id === organizationRef) ?? null;
              }
              if (organizationRef?._id) {
                return (
                  organizationLookup.find((organization) => organization._id === organizationRef._id) ?? organizationRef
                );
              }
              if (organizationRef?.id) {
                return organizationLookup.find((organization) => organization._id === organizationRef.id) ?? null;
              }
              return null;
            })
            .filter(Boolean);
        }
        break;
      }
      case 'journalist':
      case 'expert':
        newField = val;
        break;
      case 'outlet':
        newField = val?._id;
        break;
      case 'teamMember':
        newField = val;
        break;
      case 'topic':
      case 'response':
      case 'lead':
      case 'type':
      case 'date':
      case 'entryDate':
      case 'publishDate':
      case 'dueDate':
      case 'service':
      case 'unitLead':
      case 'notes':
      case 'background':
      case 'keyMessaging':
      case 'actions':
      case 'tone':
      case 'linkedInteraction':
      case 'linkedEngagement':
      case 'complexity':
      case 'status':
        newField = val;
        break;
      case 'campaign':
        newField = val.map((campaign) => {
          return campaign;
        });
        break;
      case 'units':
        newField = val.map((unit) => {
          return unit;
        });
        break;
      case 'dept':
        newField = val.map((dept) => {
          return dept?.name;
        });
        break;
      default:
        newField = val?.name;
        break;
    }
    setBulkEditFields((prevBulkEditFieldsState) => ({ ...prevBulkEditFieldsState, [col]: newField }));

    if (eventHandler.type === TrackerDataType.ENGAGEMENT) {
      if (autoOrganizationsToAdd) {
        setBulkEditFields((prev) => {
          const existingOrganizations = Array.isArray(prev.organization) ? [...prev.organization] : [];
          for (const organization of autoOrganizationsToAdd) {
            if (
              organization &&
              !existingOrganizations.some((existingOrganization) => existingOrganization._id === organization._id)
            ) {
              existingOrganizations.push(organization);
            }
          }
          return { ...prev, organization: existingOrganizations };
        });
      }

      if (organizationIdsAfterChange) {
        setBulkEditFields((prev) => {
          if (!Array.isArray(prev.communityMember)) {
            return prev;
          }

          const filteredCommunityMembers = prev.communityMember.filter((communityMember) => {
            const organizationRef = communityMember?.Organization;
            if (!organizationRef) return true;

            let organizationId = null;
            if (typeof organizationRef === 'string') {
              organizationId = organizationRef;
            } else if (organizationRef?._id) {
              organizationId = organizationRef._id;
            } else if (organizationRef?.id) {
              organizationId = organizationRef.id;
            }

            if (!organizationId) return true;
            if (organizationIdsAfterChange.length === 0) return false;

            return organizationIdsAfterChange.includes(organizationId);
          });

          return { ...prev, communityMember: filteredCommunityMembers };
        });
      }
    }

    if (col === 'outlet') {
      setBulkEditFields((prevBulkEditFieldsState) => ({ ...prevBulkEditFieldsState, journalist: null }));
    } else if (col === 'journalist') {
      setBulkEditFields((prevBulkEditFieldsState) => ({
        ...prevBulkEditFieldsState,
        outlet: findOrganizationFromContact(newField, dropdownTable['outlet'], 'Org'),
      }));
    } else if (col === 'unitLead' || col === 'expert') {
      const currentSelectedExperts = Array.isArray(newField) ? newField : [];

      const unitsFromSelectedExperts = currentSelectedExperts
        .map((expert) => {
          if (!expert || !expert.unit) {
            return null;
          }
          if (!dropdownTable || !dropdownTable['units'] || !Array.isArray(dropdownTable['units'])) {
            return null;
          }
          const unitObj = dropdownTable['units'].find((u) => u._id === expert.unit);
          return unitObj ? unitObj.name : null;
        })
        .filter(Boolean);

      setBulkEditFields((prevBulkEditFieldsState) => {
        const existingUnits =
          prevBulkEditFieldsState.units && Array.isArray(prevBulkEditFieldsState.units)
            ? prevBulkEditFieldsState.units
            : [];
        const combinedUnits = [...new Set([...existingUnits, ...unitsFromSelectedExperts])];
        return {
          ...prevBulkEditFieldsState,
          units: combinedUnits,
        };
      });
    }
  };

  const isEmptyFields = useMemo(() => {
    if (Object.keys(bulkEditFields).length === 0) {
      return true;
    }

    for (const key in bulkEditFields) {
      const value = bulkEditFields[key];
      if (value !== undefined && value !== null && !(Array.isArray(value) && value.length === 0)) {
        return false;
      }
    }

    return true;
  }, [bulkEditFields]);

  return (
    <>
      <Stack direction="row" justifyContent="space-between">
        <Box>
          <Typography align="center" fontWeight={500} fontSize="24px">
            Edit {selectedBulkEditRowIds.length} {rowType}
          </Typography>
        </Box>
        <Box>
          <LoadingButton
            className={classes.button}
            color="primary"
            variant="contained"
            loading={isSubmitBulkEditButtonLoading}
            disabled={isEmptyFields}
            onClick={() => handleSubmitBulkEdit()}
            data-cy="bulkEditQueryAdderSubmitButton"
          >
            Submit
          </LoadingButton>
          <Button
            className={classes.button}
            width={200}
            color="primary"
            variant="outlined"
            onClick={() => handleCloseBulkEditPopup()}
            data-cy="bulkEditQueryAdderCancelButton"
          >
            Cancel
          </Button>
        </Box>
      </Stack>
      <Divider className={classes.divider} />
      <Alert className={classes.alert} severity="error">
        By clicking Submit, any filled fields will be overwritten for {selectedBulkEditRowIds.length} {rowType}.
      </Alert>
      <Box className={classes.form}>
        {columns.map((col) => (
          <QueryAdderCell
            key={col}
            col={col}
            dropdownTable={dropdownTable}
            fields={bulkEditFields}
            callback={handleBulkEditFieldsChange}
            required={false}
            error={false}
            eventHandler={eventHandler}
            isBulkUpdate={true}
          />
        ))}
      </Box>
    </>
  );
}
